import type {
  GapAnalysisSection,
  GapAttribute,
} from '~/features/catalog-operations/types/catalog-operations.types';

/** Per-aspect rollup of one gap section. Every panel reads from this so the
 *  figures on screen can never drift apart from one another. */
export interface AspectRollup {
  aspect: string;
  /** Rows the retailer's own listing already carried. */
  had: GapAttribute[];
  /** Enriched rows carrying genuinely new information. */
  added: GapAttribute[];
  /** Enriched rows that semantically restate existing content. */
  restated: GapAttribute[];
  /** Summed search volume across the added rows only. */
  addedVolume: number;
}

/** One added attribute lifted out of its section, for the search-demand panel. */
export interface DemandRow {
  aspect: string;
  tag: string;
  searchVolume: number;
  competitionLevel: string | null;
}

/** Buckets each section's rows by which side they came from and, for enriched
 *  rows, whether the verdict was net-new. `isNetNew` stays tri-state — baseline
 *  rows are never counted as restatements. */
export function toAspectRollups(sections: GapAnalysisSection[]): AspectRollup[] {
  return sections.map(({ section, rows }) => {
    const had = rows.filter((row) => row.source === 'baseline');
    const added = rows.filter((row) => row.source === 'enriched' && row.isNetNew === true);
    const restated = rows.filter((row) => row.source === 'enriched' && row.isNetNew === false);

    return {
      aspect: section,
      had,
      added,
      restated,
      addedVolume: added.reduce((total, row) => total + (row.searchVolume ?? 0), 0),
    };
  });
}

/** Enough rows to rank the additions worth acting on without turning the search
 *  panel into a second table. */
export const DEMAND_ROW_LIMIT = 6;

/** Which side of the comparison a row sits on, in the vocabulary the UI uses. */
export type GapStatus = 'added' | 'restated' | 'had';

/** Monthly-search bands. Coarse on purpose — a slider implies a precision the
 *  keyword index doesn't have. */
export type VolumeBand = 'all' | 'high' | 'mid' | 'low';

export type GapSort = 'default' | 'volume' | 'new' | 'alpha';

/** Everything the filter bar controls. Held in one object so the comparison and
 *  the table can never be filtered differently from each other. */
export interface GapFilters {
  /** Debounced substring match on the attribute name. */
  query: string;
  /** Empty means "no status filter", not "match nothing". */
  statuses: GapStatus[];
  categories: string[];
  volumeBand: VolumeBand;
  /** `LOW` / `MEDIUM` / `HIGH`, matched case-insensitively. */
  competition: string[];
  sort: GapSort;
}

export const EMPTY_GAP_FILTERS: GapFilters = {
  query: '',
  statuses: [],
  categories: [],
  volumeBand: 'all',
  competition: [],
  sort: 'default',
};

/** True when anything is narrowing the view, which is what decides whether the
 *  "Clear all" control and the filtered-empty state are worth showing. */
export function hasActiveFilters(filters: GapFilters): boolean {
  return (
    filters.query.trim() !== '' ||
    filters.statuses.length > 0 ||
    filters.categories.length > 0 ||
    filters.volumeBand !== 'all' ||
    filters.competition.length > 0
  );
}

/** Inclusive lower bound / exclusive upper bound per band, in monthly searches. */
function matchesVolumeBand(volume: number, band: VolumeBand): boolean {
  if (band === 'all') return true;
  if (band === 'high') return volume >= 10_000;
  if (band === 'mid') return volume >= 1_000 && volume < 10_000;
  return volume > 0 && volume < 1_000;
}

/** Total attributes across every bucket of a rollup. */
function rollupSize(rollup: AspectRollup): number {
  return rollup.had.length + rollup.added.length + rollup.restated.length;
}

/** Attribute count across every category, for "showing N of M" labels. */
export function countRows(rollups: AspectRollup[]): number {
  return rollups.reduce((total, rollup) => total + rollupSize(rollup), 0);
}

/** Running count per status across every category, for the filter chips. The
 *  counts describe the unfiltered product so a chip never reads zero purely
 *  because another chip is active. */
export function countByStatus(rollups: AspectRollup[]): Record<GapStatus, number> {
  return rollups.reduce(
    (totals, rollup) => ({
      added: totals.added + rollup.added.length,
      restated: totals.restated + rollup.restated.length,
      had: totals.had + rollup.had.length,
    }),
    { added: 0, restated: 0, had: 0 },
  );
}

/**
 * Applies every active filter and the sort, returning rollups of the same shape
 * so consumers need no filter-awareness of their own. Categories left with no
 * rows are dropped rather than rendered as two empty columns.
 */
export function applyGapFilters(rollups: AspectRollup[], filters: GapFilters): AspectRollup[] {
  const query = filters.query.trim().toLowerCase();
  const categories = new Set(filters.categories);
  const statuses = new Set(filters.statuses);
  const competition = new Set(filters.competition.map((level) => level.toLowerCase()));

  const keep = (row: GapAttribute, status: GapStatus): boolean => {
    if (statuses.size > 0 && !statuses.has(status)) return false;
    if (query && !row.tag.toLowerCase().includes(query)) return false;

    // Keyword predicates only ever narrow enriched rows that carry a match;
    // a baseline row has no volume by definition and would always fail them.
    if (
      filters.volumeBand !== 'all' &&
      !matchesVolumeBand(row.searchVolume ?? 0, filters.volumeBand)
    )
      return false;
    if (competition.size > 0 && !competition.has((row.competitionLevel ?? '').toLowerCase()))
      return false;

    return true;
  };

  const filtered = rollups
    .filter((rollup) => categories.size === 0 || categories.has(rollup.aspect))
    .map((rollup) => {
      const added = rollup.added.filter((row) => keep(row, 'added'));
      const restated = rollup.restated.filter((row) => keep(row, 'restated'));
      const had = rollup.had.filter((row) => keep(row, 'had'));

      return {
        ...rollup,
        added,
        restated,
        had,
        addedVolume: added.reduce((total, row) => total + (row.searchVolume ?? 0), 0),
      };
    })
    .filter((rollup) => rollupSize(rollup) > 0);

  if (filters.sort === 'alpha') {
    return [...filtered].sort((a, b) => a.aspect.localeCompare(b.aspect));
  }
  if (filters.sort === 'new') {
    return [...filtered].sort(
      (a, b) => b.added.length - a.added.length || a.aspect.localeCompare(b.aspect),
    );
  }
  if (filters.sort === 'volume') {
    return [...filtered].sort(
      (a, b) => b.addedVolume - a.addedVolume || a.aspect.localeCompare(b.aspect),
    );
  }
  return filtered;
}

/** Highest-volume added attributes across every aspect. Rows without a keyword
 *  match are dropped rather than shown as zeroes — an empty result is the
 *  signal to drop the search panel entirely rather than render a blank one. */
export function toTopDemandRows(rollups: AspectRollup[], limit: number): DemandRow[] {
  return rollups
    .flatMap((rollup) =>
      rollup.added.map((row) => ({
        aspect: rollup.aspect,
        tag: row.tag,
        searchVolume: row.searchVolume ?? 0,
        competitionLevel: row.competitionLevel,
      })),
    )
    .filter((row) => row.searchVolume > 0)
    .sort((a, b) => b.searchVolume - a.searchVolume)
    .slice(0, limit);
}

/** Human-readable explanation per non-`ok` volume status. Only the volume
 *  figures are affected — the added vs restated split still stands, so this
 *  reads as a caveat rather than an error. */
export function volumeStatusNote(status: string): string | null {
  if (status === 'ok') return null;
  if (status === 'ok_semantic_exact')
    return 'Search volumes were matched by meaning, so exact brand phrases may be missed.';
  if (status === 'unconfigured')
    return 'No keyword index is set up for this tenant, so search volume and competition are unavailable.';
  if (status === 'no_subsidiary')
    return 'No subsidiary keyword index was found, so search volume and competition are unavailable.';
  return `Search volumes could not be resolved (${status}).`;
}

/** Title-cases the competition level and blanks out the "no match" sentinels. */
export function competitionLabel(level: string | null): string | null {
  if (!level) return null;
  const normalized = level.toLowerCase();
  if (normalized === 'na' || normalized === 'unknown') return null;
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

/** Compact volume for inline use next to an attribute (`27.1k`, `880`). */
export function formatCompactVolume(volume: number): string {
  if (volume < 1000) return String(Math.round(volume));
  const thousands = volume / 1000;
  return `${thousands.toFixed(thousands >= 10 ? 0 : 1).replace('.0', '')}k`;
}

/** Full-precision count for headline figures and tooltips. */
export function formatCount(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/** True when at least one row carries a keyword match, which is what decides
 *  whether the search columns and demand panel are worth rendering at all. */
export function hasKeywordData(sections: GapAnalysisSection[]): boolean {
  return sections.some((section) =>
    section.rows.some((row) => (row.searchVolume ?? 0) > 0 || row.matchedTerm != null),
  );
}
